import { makeStyles } from "@material-ui/core/styles";
import AutocompleteArrayInputEx from "components/_extensions/react-admin/ra-ui-materialui/src/input/AutocompleteArrayInputEx";
import { CustomFieldInput } from "components/CustomFields/CustomFieldInput";
import { CoolerTypeSelect } from "components/inputs/CoolerTypeSelect";
import { envSettings } from "core/EnvSettings";
import { HideableOrderField } from "entities/HideableOrderField";
import { useTerminalFieldVisibility } from "hooks/useTerminalFieldVisibility";
import React from "react";
import {
	Filter,
	ReferenceArrayInput,
	ReferenceInput,
	useListContext,
	useTranslate,
} from "react-admin";

import TextInputEx from "../../../components/_extensions/react-admin/input/TextInputEx";
import { ToggleDatesEnum, ToggleDatesFilter } from "../../../components/filters/ToggleDatesFilter";
import { ToggleInput, ToggleTitleInput } from "../../../components/filters/ToggleInputs";
import { ToggleStateFilter } from "../../../components/filters/ToggleStateFilter";
import { TERMINAL_DEFAULT_VALUE } from "../../../components/filters/ToggleTerminalsFilter";
import { CompanySelect, ExcludeCompanySelect } from "../../../components/inputs";
import { YesNoInput } from "../../../components/inputs";
import { CommodityInput } from "../../../components/inputs/CommodityInput";
import { DateInput, TimeInput } from "../../../components/inputs/DateTimeInput";
import { PortInput } from "../../../components/inputs/PortInput";
import { SelectUppercaseInput } from "../../../components/inputs/SelectUppercaseInput";
import TerminalDatesInput from "../../../components/inputs/TerminalDatesInput";
import { OrderNormalStatuses, OrderStatusActive } from "../../../entities/OrderStatus";
import { filterColors } from "../../../utils/constants";
import { ServiceInputRaw } from "../forms/inputs/ServiceInput";
import { ToggleTypeFilter } from "./ToggleTypeFilter";

export const filterDefaultValues = {
	named_eta_etd_datetime: [ToggleDatesEnum.TODAY],
	terminal_id: [TERMINAL_DEFAULT_VALUE],
};

const useStyles = makeStyles((t) => ({
	filterTerminal: {
		backgroundColor: filterColors.in_terminal,
	},
	filterSearch: {
		backgroundColor: filterColors.search,
	},
}));

const getCustomFieldFilters = (customFields) => {
	return customFields
		.filter((field) => field.is_show_on_grid)
		.map((field) => (
			<CustomFieldInput
				fieldType={field.type}
				isFilter={true}
				key={`custom_${field.key}`}
				source={`custom_fields.${field.key}`}
				label={field.name}
				resettable={true}
			/>
		));
};

const FILTERS_VISIBLE_ONLY_WHEN_SET = new Set([
	"eta_date",
	"eta_time",
	"etd_date",
	"etd_time",
	"etd_truck_id",
	"etd_truck",
	"eta_truck_id",
	"eta_truck",
]);

const STANDARD_FILTERS = new Set([
	"named_eta_etd_datetime",
	"id",
	"reference|outgoing_trip_number",
	"status",
	"si_status",
	"service",
	"eta_truck|eta_trailer|etd_truck|etd_trailer",
	"eta_3rd_party|etd_3rd_party",
	"eta_etd_date",
	"eta_driver|etd_driver",
	"cooler_type_id",
	"optimal_port_out",
]);

const CustomFilter = (props) => {
	const {
		hasCompanyFilter,
		hasOrderTypeFilter,
		isSearchMode,
		isOnlyDates,
		isDraft,
		userCompanyId,
		customFields,
		currentFilters,
		children = [],
		...rest
	} = props;

	const { setFilters } = useListContext();
	const { isFieldHidden } = useTerminalFieldVisibility(currentFilters?.terminal_id?.[0]);

	React.useEffect(() => {
		const { company_id, custom_fields, ...rest } = props.filterValues || {};
		if ("custom_fields" in props.filterValues || !("company_id" in props.filterValues)) {
			const { custom_fields, ...rest } = props.filterValues;
			setFilters(rest);
		}
	}, [userCompanyId, props.filterValues?.company_id]);

	const isVisible = (child) => {
		const source = child.props.source;

		if (FILTERS_VISIBLE_ONLY_WHEN_SET.has(source)) {
			return rest.filterValues[source] && !isDraft;
		}

		if (STANDARD_FILTERS.has(source)) {
			return !isSearchMode && !isDraft;
		}

		switch (source) {
			case "in_terminal":
			case "status_state":
				return !isSearchMode && !isOnlyDates && !isDraft;
			case "order_type":
				return !isSearchMode && hasOrderTypeFilter;
			case "is_external_draft":
			case "is_unviewed_draft":
				return false;
			case "is_search":
				return !isOnlyDates;
			case "exclude_company_id":
			case "company_id":
				return !isSearchMode && hasCompanyFilter && !isDraft;
			case "outgoing_trip_number":
				return !isSearchMode && !isDraft && !isFieldHidden("outgoing_trip_number");
			case "commodity_id":
				return !isSearchMode && !isDraft && !isFieldHidden(HideableOrderField.COMMODITY_ID);
			case "is_reefer":
				return !isSearchMode && !isDraft && envSettings.REACT_APP_FF_STORAGE_MODULE;
			case "doc_search_text":
				return isSearchMode;
			default:
				return false;
		}
	};

	const filtredChildren = children.filter((child) => child && isVisible(child));
	const customFieldFilters = getCustomFieldFilters(customFields);

	return <Filter {...rest} children={[...filtredChildren, ...customFieldFilters]} />;
};

const REACT_APP_4_HOURS_AHEAD_FILTER__HIDDEN = envSettings.REACT_APP_4_HOURS_AHEAD_FILTER__HIDDEN;

export const OrdersFilter = (props) => {
	const translate = useTranslate();
	const classes = useStyles();
	const terminalId = props.currentFilters?.terminal_id?.[0];

	return (
		<CustomFilter {...props}>
			<ToggleDatesFilter
				source="named_eta_etd_datetime"
				hiddenButtons={{
					[ToggleDatesEnum.FOUR_HOURS_AHEAD]: REACT_APP_4_HOURS_AHEAD_FILTER__HIDDEN,
				}}
				defaultValue={null}
				alwaysOn
			/>
			<ToggleInput
				source="in_terminal"
				value={true}
				alwaysOn
				key={"in_terminal"}
				label={translate("osfc.filters.toggles.in_terminal")}
				className={classes.filterTerminal}
			/>
			<ToggleStateFilter
				source="status_state"
				defaultValue={undefined}
				alwaysOn
				key={"status_state"}
			/>
			<ToggleTypeFilter source="order_type" defaultValue={undefined} alwaysOn key={"order_type"} />

			<ToggleInput
				source="is_external_draft"
				value={false}
				key="is_external_draft"
				alwaysOn
				label={translate("osfc.filters.toggles.external_draft_orders")}
				className={classes.filterTerminal}
			/>
			<ToggleInput
				source="is_unviewed_draft"
				value={false}
				key="is_unviewed_draft"
				alwaysOn
				label={translate("osfc.filters.toggles.unviewed_draft_orders")}
				className={classes.filterTerminal}
			/>
			<ToggleTitleInput
				source="is_search"
				value={true}
				key={"is_search"}
				label0={translate("osfc.filters.toggles.search")}
				label1={translate("osfc.filters.toggles.back_to_orders")}
				alwaysOn
				className={classes.filterSearch}
			/>

			<TerminalDatesInput
				component={DateInput}
				source="eta_date"
				key={"eta_date"}
				disabled
				priorityRecord={{ terminal_id: terminalId }}
			/>
			<TerminalDatesInput
				component={TimeInput}
				source="eta_time"
				key={"eta_time"}
				disabled
				priorityRecord={{ terminal_id: terminalId }}
			/>

			<TerminalDatesInput
				component={DateInput}
				source="etd_date"
				key={"etd_date"}
				disabled
				priorityRecord={{ terminal_id: terminalId }}
			/>
			<TerminalDatesInput
				component={TimeInput}
				source="etd_time"
				key={"etd_time"}
				disabled
				priorityRecord={{ terminal_id: terminalId }}
			/>

			<TextInputEx source="etd_truck_id" key={"etd_truck_id"} disabled />
			<TextInputEx source="etd_truck" key={"etd_truck"} disabled />

			<TextInputEx source="eta_truck_id" key={"eta_truck_id"} disabled />
			<TextInputEx source="eta_truck" key={"eta_truck"} disabled />

			<TextInputEx source="id" key={"id"} parse={(elem) => (elem ? [elem] : [])} />
			<TextInputEx
				label={"resources.orders.fields.reference"}
				source="reference|outgoing_trip_number"
				resettable
				key={"reference|outgoing_trip_number"}
			/>
			<ExcludeCompanySelect
				source="exclude_company_id"
				label="osfc.filters.exclude_company_id"
				allowEmpty={false}
				resettable
				key={"exclude_company_id"}
			/>
			<CompanySelect
				source="company_id"
				emptyValue={null}
				emptyText="All Companies"
				defaultValue={null}
				allowEmpty
				resettable
				key={"company_id"}
			/>

			<ReferenceArrayInput
				source="status"
				reference="order-statuses"
				resettable
				key="status"
				filter={{ id: OrderNormalStatuses }}
			>
				{props.isOnlyDates ? (
					<AutocompleteArrayInputEx
						allowEmpty={false}
						allowedIds={OrderStatusActive}
						optionText="name"
						debounce={500}
						resettable
					/>
				) : (
					<AutocompleteArrayInputEx
						allowEmpty={false}
						showUppercase
						optionText="name"
						debounce={500}
						resettable
					/>
				)}
			</ReferenceArrayInput>
			<ReferenceInput
				label="osfc.filters.si_status"
				source="si_status"
				reference="outgoing-doc-statuses"
				resettable
				key={"status"}
			>
				<SelectUppercaseInput optionText="name" />
			</ReferenceInput>
			<TextInputEx source="outgoing_trip_number" resettable key={"outgoing_trip_number"} />
			<ServiceInputRaw
				source="service"
				resettable
				key={"service"}
				terminalId={props.currentFilters?.terminal_id?.[0]}
			/>
			<TextInputEx
				label="Truck"
				source="eta_truck|eta_trailer|etd_truck|etd_trailer"
				resettable
				key={"Truck"}
			/>
			<CompanySelect
				label="Transporter"
				source="eta_3rd_party|etd_3rd_party"
				emptyValue={null}
				refer="transporters"
				emptyText="All Companies"
				defaultValue={null}
				allowEmpty
				resettable
				key={"3rd_party"}
				filter={{ no_range: true }}
			/>
			<DateInput source="eta_etd_date" margin="dense" resettable key={"Date"} label="Date" />

			<TextInputEx label="Driver" source="eta_driver|etd_driver" resettable key={"driver"} />
			<PortInput
				source="optimal_port_out"
				resettable
				key={"optimal_port_out"}
				filter={terminalId ? { terminal_id: [terminalId] } : {}}
			/>
			<CommodityInput source="commodity_id" resettable style={{ minWidth: "140px" }} />
			<CoolerTypeSelect resettable key={"cooler_type_id"} uppercase />
			{envSettings.REACT_APP_FF_STORAGE_MODULE && <YesNoInput source="is_reefer" resettable />}
			<TextInputEx
				label={translate("osfc.filters.toggles.search")}
				source="doc_search_text"
				alwaysOn
			/>
		</CustomFilter>
	);
};
